// components/GeneralLedger.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import generalLedgerAPI from '../api/generalLedgerAPI';
import chartOfAccountsAPI from '../api/chartOfAccountsAPI';
import useCompanyContext from '../hooks/useCompanyContext';
import ReportToolbar from '../shared/ReportToolbar';
import { computePeriodRange } from '../shared/PeriodSelect';

const GeneralLedger = () => {
  const { accountingClientId: contextClientId } = useCompanyContext();
  const accountingClientId = contextClientId || null;

  const [accountingClientName, setAccountingClientName] = useState(
    () => localStorage.getItem('accountingClientName') || ''
  );
  useEffect(() => {
    setAccountingClientName(localStorage.getItem('accountingClientName') || '');
  }, [accountingClientId]);

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState(() => ({ preset: 'YTD', ...computePeriodRange('YTD') }));
  const [filters, setFilters] = useState({ account: '' });

  // Chart-of-accounts search state
  const [accountSearch, setAccountSearch]         = useState('');
  const [accountSuggestions, setAccountSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions]     = useState(false);
  const accountSearchRef = useRef(null);
  const debounceRef      = useRef(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 500, total: 0, pages: 0 });
  const [fiscalYear, setFiscalYear] = useState(() => {
    const id = localStorage.getItem('accountingClientId');
    return id ? (localStorage.getItem(`activeFiscalYear:${id}`) || null) : null;
  });

  // Sync fiscalYear when the active client changes via company switcher.
  useEffect(() => {
    if (accountingClientId) {
      setFiscalYear(localStorage.getItem(`activeFiscalYear:${accountingClientId}`) || null);
    }
  }, [accountingClientId]);

  useEffect(() => {
    const onFYChange = () => {
      const id = accountingClientId || localStorage.getItem('accountingClientId');
      if (id) setFiscalYear(localStorage.getItem(`activeFiscalYear:${id}`) || null);
    };
    window.addEventListener('fiscalYear:changed', onFYChange);
    return () => window.removeEventListener('fiscalYear:changed', onFYChange);
  }, [accountingClientId]);

  const fetchGeneralLedger = async (page = 1) => {
    const clientId = accountingClientId;
    setLoading(true);
    setError('');
    try {
      const params = {
        page,
        limit: pagination.limit,
        ...(period.startDate && { startDate: period.startDate }),
        ...(period.endDate && { endDate: period.endDate }),
        ...(filters.account && { account: filters.account }),
        ...(clientId && { accountingClientId: clientId }),
      };
      const response = await generalLedgerAPI.getAll(params);
      if (response.success) {
        setEntries(response.data);
        setPagination(prev => ({
          ...prev,
          page: response.pagination.page,
          total: response.pagination.total,
          pages: response.pagination.pages,
        }));
      } else {
        setError(response.message || 'Failed to fetch general ledger entries');
      }
    } catch (err) {
      setError(typeof err === 'string' ? err : (err.message || 'Error fetching general ledger entries'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGeneralLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountingClientId, fiscalYear, period.startDate, period.endDate, filters.account]);

  // Debounced chart-of-accounts search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!accountSearch.trim()) { setAccountSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await chartOfAccountsAPI.search(accountSearch.trim());
        const accounts = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setAccountSuggestions(accounts);
        setShowSuggestions(accounts.length > 0);
      } catch { setAccountSuggestions([]); }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [accountSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (accountSearchRef.current && !accountSearchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelectAccount = (acc) => {
    setAccountSearch(`${acc.accountNumber} — ${acc.accountName}`);
    setFilters(p => ({ ...p, account: acc._id }));
    setShowSuggestions(false);
  };

  const handleClearAccount = () => {
    setAccountSearch('');
    setFilters(p => ({ ...p, account: '' }));
    setAccountSuggestions([]);
    setShowSuggestions(false);
  };

  const nf = new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmt = (amount) => nf.format(Math.abs(amount || 0));

  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-PH', { month: 'short', day: '2-digit', year: 'numeric' });
  };

  // ── Group entries by account ─────────────────────────────────────────────
  const grouped = React.useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      const key = e.account?._id || 'unknown';
      if (!map.has(key)) {
        map.set(key, {
          accountNumber: e.account?.accountNumber || '',
          accountName: e.account?.accountName || 'Unknown Account',
          entries: [],
        });
      }
      map.get(key).entries.push(e);
    }
    // Sort accounts by account number
    return Array.from(map.values()).sort((a, b) =>
      (a.accountNumber || '').localeCompare(b.accountNumber || '', undefined, { numeric: true })
    );
  }, [entries]);

  // ── CSV Export ────────────────────────────────────────────────────────────
  const exportToCSV = () => {
    const rows = [['Account #', 'Account Name', 'Date', 'Description', 'Reference', 'JE #', 'Debit', 'Credit', 'Balance']];
    for (const group of grouped) {
      for (const e of group.entries) {
        const jeRef = e.journalEntry?.referenceNumber || e.referenceNumber || '';
        const jeNum = e.journalEntry?.entryNumber || '';
        rows.push([
          group.accountNumber,
          `"${group.accountName}"`,
          fmtDate(e.transactionDate),
          `"${e.description || ''}"`,
          jeRef,
          jeNum,
          parseFloat((e.debit || 0).toFixed(2)),
          parseFloat((e.credit || 0).toFixed(2)),
          parseFloat((e.runningBalance || 0).toFixed(2)),
        ]);
      }
    }
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `general-ledger-${period.startDate}-to-${period.endDate}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Print ─────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    let body = '';
    for (const group of grouped) {
      const openingBalance = group.entries.length > 0
        ? (group.entries[0].runningBalance || 0) - (group.entries[0].debit || 0) + (group.entries[0].credit || 0)
        : 0;
      const closingBalance = group.entries.length > 0
        ? group.entries[group.entries.length - 1].runningBalance || 0
        : 0;

      const rows = group.entries.map(e => {
        const jeRef = e.journalEntry?.referenceNumber || e.referenceNumber || '';
        const jeNum = e.journalEntry?.entryNumber || '';
        const refLabel = [jeRef, jeNum].filter(Boolean).join(' · ');
        return `<tr>
          <td style="padding:3px 8px;white-space:nowrap;">${fmtDate(e.transactionDate)}</td>
          <td style="padding:3px 8px;">${e.description || ''}</td>
          <td style="padding:3px 8px;white-space:nowrap;color:#0d9488;">${refLabel}</td>
          <td style="padding:3px 8px;text-align:right;font-family:monospace;color:${e.debit > 0 ? '#059669' : '#9ca3af'};">${e.debit > 0 ? fmt(e.debit) : '—'}</td>
          <td style="padding:3px 8px;text-align:right;font-family:monospace;color:${e.credit > 0 ? '#dc2626' : '#9ca3af'};">${e.credit > 0 ? fmt(e.credit) : '—'}</td>
          <td style="padding:3px 8px;text-align:right;font-family:monospace;">${fmt(e.runningBalance)}</td>
        </tr>`;
      }).join('');

      const periodNet = group.entries.reduce((s, e) => s + (e.debit || 0) - (e.credit || 0), 0);
      body += `
        <tr style="background:#f3f4f6;border-top:2px solid #d1d5db;">
          <td colspan="2" style="padding:6px 8px;font-weight:bold;font-size:12px;">
            ${group.accountNumber} &nbsp;—&nbsp; ${group.accountName}
          </td>
          <td style="padding:6px 8px;text-align:right;font-size:10px;color:#6b7280;">
            <span style="text-transform:uppercase;letter-spacing:.04em;">Net Change</span>
            <span style="font-family:monospace;font-weight:600;margin-left:4px;">${fmt(periodNet)}</span>
          </td>
          <td colspan="2" style="padding:6px 8px;text-align:right;font-size:10px;color:#6b7280;">
            <span style="text-transform:uppercase;letter-spacing:.04em;">Closing Balance</span>
            <span style="font-family:monospace;font-weight:bold;font-size:12px;color:#111;margin-left:4px;">${fmt(closingBalance)}</span>
          </td>
        </tr>
        <tr style="background:#f9fafb;color:#6b7280;font-size:10px;">
          <td style="padding:3px 8px;" colspan="4">Opening Balance</td>
          <td style="padding:3px 8px;text-align:right;font-family:monospace;">${fmt(openingBalance)}</td>
        </tr>
        ${rows}
        <tr style="background:#f9fafb;border-top:1px solid #9ca3af;">
          <td colspan="3" style="padding:4px 8px;font-weight:bold;">Closing Balance</td>
          <td style="padding:4px 8px;text-align:right;font-size:10px;color:#6b7280;">
            <span style="text-transform:uppercase;letter-spacing:.04em;">Net Change</span>
            <span style="font-family:monospace;font-weight:600;margin-left:4px;">${fmt(periodNet)}</span>
          </td>
          <td style="padding:4px 8px;text-align:right;font-family:monospace;font-weight:bold;">${fmt(closingBalance)}</td>
        </tr>`;
    }

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>General Ledger</title>
<style>
body{font-family:'Courier New',monospace;font-size:11px;color:#111;margin:32px}
.hdr{text-align:center;margin-bottom:20px}
.hdr h1{font-size:16px;margin:0 0 2px;font-weight:bold}
.hdr .sub{font-size:11px;color:#6b7280;margin-top:4px}
table{width:100%;border-collapse:collapse}
th{padding:4px 8px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.04em;border-bottom:2px solid #d1d5db;background:#f3f4f6}
th.r{text-align:right}
tr:hover{background:#fafafa}
.tb{text-align:center;margin-bottom:16px}
.tb button{padding:6px 16px;font-size:11px;cursor:pointer;background:#0d9488;color:#fff;border:none;border-radius:4px}
@media print{.tb{display:none}@page{margin:15mm}}
</style></head><body>
<div class="tb"><button onclick="window.print()">🖨 Print / Save as PDF</button></div>
<div class="hdr">
${accountingClientName ? `<div style="font-size:14px;font-weight:bold;margin-bottom:2px;">${accountingClientName}</div>` : ''}
<h1>GENERAL LEDGER</h1>
<div class="sub">${period.startDate} to ${period.endDate}${fiscalYear ? ` | Fiscal Year: ${fiscalYear}` : ''}</div>
</div>
<table>
<thead><tr>
<th>Date</th><th>Description</th><th>Ref # / JE #</th>
<th class="r">Debit</th><th class="r">Credit</th><th class="r">Balance</th>
</tr></thead>
<tbody>${body}</tbody>
</table>
</body></html>`;

    const win = window.open('', '_blank', 'width=1000,height=900');
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 500);
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">

        {/* Controls */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">GENERAL LEDGER</h1>
          <div className="flex justify-center">
            <ReportToolbar
              period={period}
              onPeriodChange={setPeriod}
              fiscalYear={fiscalYear}
              onExportCSV={entries.length > 0 ? exportToCSV : undefined}
              onPrint={entries.length > 0 ? handlePrint : undefined}
              onRefresh={() => fetchGeneralLedger(1)}
              refreshing={loading}
              extra={
                <div className="relative" ref={accountSearchRef}>
                  <input
                    type="text"
                    value={accountSearch}
                    placeholder="Search account by name or number…"
                    onChange={e => {
                      setAccountSearch(e.target.value);
                      if (!e.target.value) handleClearAccount();
                    }}
                    onFocus={() => accountSuggestions.length > 0 && setShowSuggestions(true)}
                    className="px-3 py-2 pr-7 border border-ink-line rounded-md text-ink text-sm w-56 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                  {accountSearch && (
                    <button
                      type="button"
                      onClick={handleClearAccount}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-body hover:text-ink leading-none"
                      tabIndex={-1}
                    >
                      ×
                    </button>
                  )}
                  {showSuggestions && (
                    <ul className="absolute z-50 mt-1 w-72 bg-white border border-ink-line rounded-md shadow-card-hover max-h-56 overflow-y-auto text-sm text-left">
                      {accountSuggestions.map(acc => (
                        <li
                          key={acc._id}
                          onMouseDown={() => handleSelectAccount(acc)}
                          className="px-3 py-2 hover:bg-ink-canvas cursor-pointer"
                        >
                          <span className="font-mono text-ink-body mr-1">{acc.accountNumber}</span>
                          <span className="text-ink">{acc.accountName}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              }
            />
          </div>
        </div>

        {error && <div className="mb-6 rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</div>}

        {loading && (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600" />
          </div>
        )}

        {/* QB-style ledger body */}
        {!loading && entries.length > 0 && (
          <div className="bg-white font-mono text-sm">

            {/* Report header */}
            <div className="text-center py-6 border-b-2 border-gray-300">
              {accountingClientName && (
                <div className="text-lg font-bold text-gray-900 mb-1">{accountingClientName}</div>
              )}
              <div className="text-xl font-bold tracking-wide text-gray-900">GENERAL LEDGER</div>
              <div className="text-xs text-gray-500 mt-1">
                {period.startDate} to {period.endDate}
                {fiscalYear && <> &nbsp;|&nbsp; Fiscal Year: {fiscalYear}</>}
              </div>
            </div>

            {/* Account groups */}
            {grouped.map((group) => {
              const openingBalance = group.entries.length > 0
                ? (group.entries[0].runningBalance || 0) - (group.entries[0].debit || 0) + (group.entries[0].credit || 0)
                : 0;
              const closingBalance = group.entries.length > 0
                ? group.entries[group.entries.length - 1].runningBalance || 0
                : 0;
              // Net activity within the filtered period (what changed, not the cumulative balance)
              const periodNet = group.entries.reduce(
                (s, e) => s + (e.debit || 0) - (e.credit || 0), 0
              );

              return (
                <div key={group.accountNumber} className="border-b border-gray-200">
                  {/* Account header — period net + closing balance inline */}
                  <div className="grid grid-cols-12 bg-gray-100 border-t-2 border-gray-300 px-4 py-2 items-center">
                    <div className="col-span-6 font-bold text-gray-800 text-sm">
                      {group.accountNumber} &nbsp;—&nbsp; {group.accountName}
                    </div>
                    <div className="col-span-3 text-right text-xs text-gray-500">
                      <span className="uppercase tracking-wide">Net Change</span>&nbsp;
                      <span className="font-mono font-semibold">{fmt(periodNet)}</span>
                    </div>
                    <div className="col-span-3 text-right text-xs text-gray-500">
                      <span className="uppercase tracking-wide">Closing Balance</span>&nbsp;
                      <span className="font-mono font-bold text-gray-800 text-sm">{fmt(closingBalance)}</span>
                    </div>
                  </div>

                  {/* Column headers */}
                  <div className="grid grid-cols-12 bg-gray-50 px-4 py-1 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    <div className="col-span-2">Date</div>
                    <div className="col-span-3">Description</div>
                    <div className="col-span-2">Ref # / JE #</div>
                    <div className="col-span-1 text-right">Debit</div>
                    <div className="col-span-2 text-right">Credit</div>
                    <div className="col-span-2 text-right">Balance</div>
                  </div>

                  {/* Opening balance */}
                  <div className="grid grid-cols-12 px-4 py-1 border-b border-gray-100 text-xs text-gray-400 italic">
                    <div className="col-span-5">Opening Balance</div>
                    <div className="col-span-2" />
                    <div className="col-span-1" />
                    <div className="col-span-2" />
                    <div className="col-span-2 text-right">{fmt(openingBalance)}</div>
                  </div>

                  {/* Transaction rows */}
                  {group.entries.map((e, idx) => {
                    const jeId = typeof e.journalEntry === 'object' ? e.journalEntry?._id : e.journalEntry;
                    const jeRef = e.journalEntry?.referenceNumber || e.referenceNumber || '';
                    const jeNum = e.journalEntry?.entryNumber || '';
                    const memo = e.description || '';

                    const refLabel = jeNum; //[jeRef, jeNum].filter(Boolean).join(' · ');

                    const isVoidReversal = e.source === 'void';
                    const isVoidedOriginal = !isVoidReversal && e.journalEntry?.status === 'void';

                    return (
                      <div key={e._id || idx}
                        className={`grid grid-cols-12 px-4 py-1.5 border-b border-gray-100 hover:bg-teal-50 transition-colors ${isVoidReversal ? 'bg-red-50' : isVoidedOriginal ? 'bg-amber-50' : ''}`}>
                        <div className="col-span-2 text-gray-600 text-xs whitespace-nowrap">
                          {fmtDate(e.transactionDate)}
                        </div>
                        <div className="col-span-3 text-gray-800">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {isVoidReversal && (
                              <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-100 text-red-700 border border-red-300 leading-none tracking-wide">VOID</span>
                            )}
                            {isVoidedOriginal && (
                              <span className="inline-block px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-700 border border-amber-300 leading-none tracking-wide">VOIDED</span>
                            )}
                            <span>{memo || <span className="text-gray-400 italic">—</span>}</span>
                          </div>
                        </div>
                        <div className="col-span-2 text-xs">
                          {jeId && refLabel ? (
                            <Link to={`/journal-entry/${jeId}`}
                              className="text-teal-700 hover:text-teal-900 hover:underline font-medium whitespace-nowrap">
                              {refLabel}
                            </Link>
                          ) : refLabel ? (
                            <span className="text-gray-500 whitespace-nowrap">{refLabel}</span>
                          ) : null}
                        </div>
                        <div className="col-span-1 text-right text-teal-700">
                          {e.debit > 0 ? fmt(e.debit) : <span className="text-gray-300">—</span>}
                        </div>
                        <div className="col-span-2 text-right text-red-600">
                          {e.credit > 0 ? fmt(e.credit) : <span className="text-gray-300">—</span>}
                        </div>
                        <div className="col-span-2 text-right font-semibold text-gray-900">
                          {fmt(e.runningBalance)}
                        </div>
                      </div>
                    );
                  })}

                  {/* Closing balance */}
                  <div className="grid grid-cols-12 px-4 py-2 bg-gray-50 border-t border-gray-300 font-bold text-sm">
                    <div className="col-span-6 text-gray-700">Closing Balance</div>
                    <div className="col-span-3 text-right text-gray-500 font-normal text-xs">
                      <span className="uppercase tracking-wide">Net Change</span>&nbsp;
                      <span className="font-mono font-semibold">{fmt(periodNet)}</span>
                    </div>
                    <div className="col-span-3 text-right text-gray-900">{fmt(closingBalance)}</div>
                  </div>
                </div>
              );
            })}

            {/* Pagination (if > 500 entries) */}
            {pagination.pages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm">
                <div className="text-gray-500">
                  Page <span className="font-semibold">{pagination.page}</span> of{' '}
                  <span className="font-semibold">{pagination.pages}</span> ({pagination.total} total)
                </div>
                <div className="flex gap-2">
                  <button onClick={() => fetchGeneralLedger(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-40 text-sm">
                    Previous
                  </button>
                  <button onClick={() => fetchGeneralLedger(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="px-3 py-1 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 disabled:opacity-40 text-sm">
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="text-center py-16 text-gray-400 font-mono text-sm">
            No entries found. Adjust your date range and click Search.
          </div>
        )}

      </div>
    </div>
  );
};

export default GeneralLedger;